import { Session } from './session';

const CLASS_IMAGE_BASE = '/assets/images/';

/** One class row from getClasses; matches an element in the `classes` array. */
export interface ApiClass {
  regSeq?: number;
  clsSeq?: number;
  siteCode?: string;
  name?: string;
  cost?: number;
  group?: string;
  groupDescription?: string;
  description?: string;
  costcenter?: string;
  image1?: string;
  image2?: string;
  image3?: string;
  image4?: string;
  active?: number;
  /** 0 = no payment required to register; enroll immediately on saveRegistration. */
  payToRegister?: number;
  onDemand?: number;
  onDemandLink?: string;
  classSessionsDays?: number;
  sessionCount?: number;
  sessions?: ApiClassSession[];
}

export interface ApiClassSessionDate {
  sdSeq?: number;
  sesSeq?: number;
  duration?: string;
  meetingLink?: string;
  durationDescription?: string;
  sessionDateTime?: string;
  slSeq?: number;
  sessionDateLocName?: string;
  sessionDateLocAddress?: string;
  sessionDateLocCity?: string;
  sessionDateLocState?: string;
  sessionDateLocZip?: string;
  sessionDateLocRoom?: string;
  sessionDateLocDirections?: string;
}

export interface ApiClassSession {
  sesSeq?: number;
  clsSeq?: number;
  type?: string;
  typeDescription?: string;
  startDateTime?: string;
  status?: string;
  capacity?: number;
  registrations?: number;
  available?: number;
  cutoffDays?: number;
  instructorName?: string;
  slSeq?: number;
  active?: number;
  sessionLocName?: string;
  sessionLocAddress?: string;
  sessionLocCity?: string;
  sessionLocState?: string;
  sessionLocZip?: string;
  sessionLocRoom?: string;
  sessionLocDirections?: string;
  sessionDates?: ApiClassSessionDate | ApiClassSessionDate[];
}

export interface ClassImage {
  publicUrl?: string;
  name?: string;
}

/** API class plus optional patient-landing card fields from router state. */
export type ClassWithLegacyCardFields = Omit<ApiClass, 'sessions'> & {
  id?: string;
  name?: string;
  classImage?: string;
  /** @deprecated Use classImage; kept for existing card/nav state. */
  courseImage?: string;
  audience?: string;
  description?: string;
  eventType?: string;
  /** @deprecated Use groupDescription; kept for existing card/nav state. */
  courseCategory?: string;
  category?: string;
  sessions?: ApiClassSession[] | Session[];
  /** Normalized sessionType bucket from patient-landing split cards (`clsSeq::virtual`). */
  sessionTypeKey?: string;
  additionalDateText?: string;
  earliestSessionDateIso?: string;
  earliestSessionCostLabel?: string;
  earliestSessionLocationLabel?: string;
};

/** Raw envelope from getClasses / GET class. */
export interface GetClassesApiEnvelope {
  x_service?: string;
  x_status?: string;
  x_count?: string | null;
  x_msg?: string;
  Classes?: unknown;
  classes?: unknown;
}

export interface ClassResponse {
  classes: ApiClass[];
  x_service: string;
  x_status: string;
  x_count: string;
  x_msg: string;
}

export function classRecordId(c: ApiClass): string {
  if (c.clsSeq != null && Number.isFinite(c.clsSeq)) {
    return String(c.clsSeq);
  }
  return '';
}

/** Strip `clsSeq::sessionType` suffix from split listing cards. */
export function classDisplayBaseId(c: ClassWithLegacyCardFields): string {
  const apiId = classRecordId(c);
  if (apiId) {
    return apiId;
  }
  const id = (c.id ?? '').trim();
  const sep = id.indexOf('::');
  return sep >= 0 ? id.slice(0, sep) : id;
}

export function classDisplayId(c: ClassWithLegacyCardFields): string {
  return classDisplayBaseId(c);
}

export function normalizeSessionTypeKey(value: string | undefined): string {
  const normalized = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
  if (normalized === 'inperson') {
    return 'in person';
  }
  return normalized || 'other';
}

export function classDisplayName(c: ClassWithLegacyCardFields): string {
  return (c.name ?? '').trim();
}

export function classDisplayImageUrl(c: ClassWithLegacyCardFields): string {
  return (c.classImage ?? c.courseImage ?? '').trim() || classCardImageUrl(c);
}

function normalizeImageMatchKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');
}

function isLogoOrBrandImageName(name: string): boolean {
  const n = name.trim().toLowerCase();
  return /\blogo\b/.test(n) || /\bicon\b/.test(n) || /\bbrand\b/.test(n);
}

function isGalleryImageName(name: string): boolean {
  return /\bgallery\b/i.test(name);
}

function scoreClassCardImage(className: string, imageName: string): number {
  let score = 0;
  if (isGalleryImageName(imageName)) {
    score -= 50;
  } else {
    score += 40;
  }
  const normClass = normalizeImageMatchKey(className);
  const normImage = normalizeImageMatchKey(imageName);
  const baseName = imageName.replace(/\s*-\s*gallery\s*/i, '').trim();
  const normBase = normalizeImageMatchKey(baseName);
  if (normClass && normImage) {
    if (normImage === normClass || normBase === normClass) {
      score += 100;
    } else if (normImage.includes(normClass) || normClass.includes(normImage)) {
      score += 60;
    } else if (normBase.includes(normClass) || normClass.includes(normBase)) {
      score += 70;
    }
  }
  return score;
}

function resolveClassImageUrl(filename: string): string {
  const name = filename.trim();
  if (!name) {
    return '';
  }
  // Root-absolute assets so nested routes (e.g. /tabs/admin-landing/...) resolve correctly.
  if (/^assets\//i.test(name)) {
    return `/${name}`;
  }
  if (/^(https?:|\/|\.\/|data:|blob:)/i.test(name)) {
    return name;
  }
  return `${CLASS_IMAGE_BASE}${name}`;
}

export function classImageList(c: ApiClass): ClassImage[] {
  const names = [c.image1, c.image2, c.image3, c.image4]
    .map((img) => (img ?? '').trim())
    .filter(Boolean);
  return names.map((name) => ({
    name,
    publicUrl: resolveClassImageUrl(name)
  }));
}

/** First configured class image (image1–image4), resolved for display. */
export function classPrimaryImageUrl(c: ApiClass): string {
  for (const raw of [c.image1, c.image2, c.image3, c.image4]) {
    const url = resolveClassImageUrl((raw ?? '').trim());
    if (url) {
      return url;
    }
  }
  return '';
}

/**
 * Best image for listing cards: skip logos, prefer the main class image over gallery variants.
 */
export function classCardImageUrl(c: ApiClass): string {
  const images = classImageList(c);
  if (!images.length) {
    return '';
  }
  const className = (c.name ?? '').trim();
  const candidates = images
    .map((img) => ({
      url: (img?.publicUrl ?? '').trim(),
      name: (img?.name ?? '').trim()
    }))
    .filter((img) => img.url && !isLogoOrBrandImageName(img.name));

  if (candidates.length === 0) {
    return '';
  }

  const ranked = [...candidates].sort(
    (a, b) => scoreClassCardImage(className, b.name) - scoreClassCardImage(className, a.name)
  );
  return ranked[0]?.url ?? '';
}

export function hasClassCardImage(c: ApiClass): boolean {
  return !!classCardImageUrl(c).trim();
}

/** Non-logo image URLs for detail thumbnails (card image first, then remaining). */
export function classGalleryImageUrls(c: ApiClass): string[] {
  const images = classImageList(c);
  if (!images.length) {
    return [];
  }
  const className = (c.name ?? '').trim();
  const candidates = images
    .map((img) => ({
      url: (img?.publicUrl ?? '').trim(),
      name: (img?.name ?? '').trim()
    }))
    .filter((img) => img.url && !isLogoOrBrandImageName(img.name));
  const ranked = [...candidates].sort(
    (a, b) => scoreClassCardImage(className, b.name) - scoreClassCardImage(className, a.name)
  );
  return [...new Set(ranked.map((img) => img.url))];
}

function str(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  if (v == null || v === '') {
    return '';
  }
  return String(v);
}

function num(row: Record<string, unknown>, key: string): number | undefined {
  const v = row[key];
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function parseClassSessionDateRow(row: Record<string, unknown> | undefined): ApiClassSessionDate | undefined {
  if (!row || typeof row !== 'object') {
    return undefined;
  }
  return {
    sdSeq: num(row, 'sdSeq'),
    sesSeq: num(row, 'sesSeq'),
    duration: str(row, 'duration') || undefined,
    meetingLink: str(row, 'meetingLink') || undefined,
    durationDescription: str(row, 'durationDescription') || undefined,
    sessionDateTime: str(row, 'sessionDateTime') || undefined,
    slSeq: num(row, 'slSeq'),
    sessionDateLocName: str(row, 'sessionDateLocName') || undefined,
    sessionDateLocAddress: str(row, 'sessionDateLocAddress') || undefined,
    sessionDateLocCity: str(row, 'sessionDateLocCity') || undefined,
    sessionDateLocState: str(row, 'sessionDateLocState') || undefined,
    sessionDateLocZip: str(row, 'sessionDateLocZip') || undefined,
    sessionDateLocRoom: str(row, 'sessionDateLocRoom') || undefined,
    sessionDateLocDirections: str(row, 'sessionDateLocDirections') || undefined
  };
}

function parseClassSessionDatesField(
  raw: unknown
): ApiClassSessionDate | ApiClassSessionDate[] | undefined {
  if (raw == null) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    const parsed = raw
      .map((entry) =>
        entry && typeof entry === 'object'
          ? parseClassSessionDateRow(entry as Record<string, unknown>)
          : undefined
      )
      .filter((entry): entry is ApiClassSessionDate => !!entry);
    if (!parsed.length) {
      return undefined;
    }
    return parsed.length === 1 ? parsed[0] : parsed;
  }
  if (typeof raw === 'object') {
    return parseClassSessionDateRow(raw as Record<string, unknown>);
  }
  return undefined;
}

function parseClassSessionRow(row: Record<string, unknown>): ApiClassSession {
  const sessionDates = parseClassSessionDatesField(row['sessionDates']);
  return {
    sesSeq: num(row, 'sesSeq'),
    clsSeq: num(row, 'clsSeq'),
    type: str(row, 'type') || undefined,
    typeDescription: str(row, 'typeDescription') || undefined,
    startDateTime: str(row, 'startDateTime') || undefined,
    status: str(row, 'status') || undefined,
    capacity: num(row, 'capacity'),
    registrations: num(row, 'registrations'),
    available: num(row, 'available'),
    cutoffDays: num(row, 'cutoffDays'),
    instructorName: str(row, 'instructorName') || undefined,
    slSeq: num(row, 'slSeq'),
    active: num(row, 'active'),
    sessionLocName: str(row, 'sessionLocName') || undefined,
    sessionLocAddress: str(row, 'sessionLocAddress') || undefined,
    sessionLocCity: str(row, 'sessionLocCity') || undefined,
    sessionLocState: str(row, 'sessionLocState') || undefined,
    sessionLocZip: str(row, 'sessionLocZip') || undefined,
    sessionLocRoom: str(row, 'sessionLocRoom') || undefined,
    sessionLocDirections: str(row, 'sessionLocDirections') || undefined,
    sessionDates
  };
}

export function classFromApiRow(row: Record<string, unknown>): ApiClass {
  const sessionsRaw = row['sessions'];
  const sessions = Array.isArray(sessionsRaw)
    ? (sessionsRaw as Record<string, unknown>[]).map(parseClassSessionRow)
    : undefined;
  return {
    clsSeq: num(row, 'clsSeq'),
    siteCode: str(row, 'siteCode') || undefined,
    name: str(row, 'name') || undefined,
    cost: num(row, 'cost'),
    group: str(row, 'group') || undefined,
    groupDescription: str(row, 'groupDescription') || undefined,
    description: str(row, 'description') || undefined,
    costcenter: str(row, 'costcenter') || undefined,
    image1: str(row, 'image1') || undefined,
    image2: str(row, 'image2') || undefined,
    image3: str(row, 'image3') || undefined,
    image4: str(row, 'image4') || undefined,
    active: num(row, 'active'),
    payToRegister: num(row, 'payToRegister'),
    onDemand: num(row, 'onDemand'),
    onDemandLink: str(row, 'onDemandLink') || undefined,
    classSessionsDays: num(row, 'classSessionsDays'),
    sessionCount: num(row, 'sessionCount'),
    sessions: sessions?.length ? sessions : undefined
  };
}

/** Unwrap classes JSON string or object and return the inner row list. */
export function extractClassRowList(classesField: unknown): Record<string, unknown>[] {
  if (classesField == null) {
    return [];
  }
  let parsed: unknown = classesField;
  if (typeof classesField === 'string') {
    const trimmed = classesField.trim();
    if (!trimmed) {
      return [];
    }
    try {
      parsed = JSON.parse(trimmed) as unknown;
    } catch {
      return [];
    }
  }
  if (Array.isArray(parsed)) {
    return parsed as Record<string, unknown>[];
  }
  if (parsed && typeof parsed === 'object') {
    const data = (parsed as Record<string, unknown>)['data'];
    if (Array.isArray(data)) {
      return data as Record<string, unknown>[];
    }
  }
  return [];
}

export function parseApiDateTime(value: string | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) {
    return '';
  }
  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) {
    return direct.toISOString();
  }
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return raw;
  }
  const month = Number(match[1]) - 1;
  const day = Number(match[2]);
  const year = Number(match[3]);
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const meridiem = match[6].toUpperCase();
  if (meridiem === 'PM' && hour < 12) {
    hour += 12;
  }
  if (meridiem === 'AM' && hour === 12) {
    hour = 0;
  }
  const parsed = new Date(year, month, day, hour, minute);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString();
}

function firstSessionDate(s: ApiClassSession): ApiClassSessionDate | undefined {
  const raw = s.sessionDates;
  if (!raw) {
    return undefined;
  }
  return Array.isArray(raw) ? raw[0] : raw;
}

function sessionLocationParts(s: ApiClassSession): string[] {
  const sd = firstSessionDate(s);
  const parts = [
    s.sessionLocName || sd?.sessionDateLocName,
    s.sessionLocAddress || sd?.sessionDateLocAddress,
    s.sessionLocCity || sd?.sessionDateLocCity,
    s.sessionLocState || sd?.sessionDateLocState,
    s.sessionLocZip || sd?.sessionDateLocZip
  ]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);
  return parts;
}

/** Map embedded {@link ApiClassSession} rows to {@link Session} for UI that still uses the session model. */
export function mapClassSessionToSession(clsSeq: string, s: ApiClassSession, classCost?: number): Session {
  const sd = firstSessionDate(s);
  const locationParts = sessionLocationParts(s);
  const street = (s.sessionLocAddress || sd?.sessionDateLocAddress || '').trim();
  const city = (s.sessionLocCity || sd?.sessionDateLocCity || '').trim();
  const state = (s.sessionLocState || sd?.sessionDateLocState || '').trim();
  const postalCode = (s.sessionLocZip || sd?.sessionDateLocZip || '').trim();
  const instructor = (s.instructorName ?? '').trim();
  const startRaw = (s.startDateTime || sd?.sessionDateTime || '').trim();
  const status = (s.status ?? '').trim().toUpperCase();
  // API often sends active: null for ACTIVE sessions — treat null/undefined as active.
  const isActive =
    s.active !== 0 && (status === '' || status === 'ACTIVE');
  return {
    url: '',
    type: (s.type ?? '').trim(),
    attributes: { type: '', url: '' },
    id: s.sesSeq != null ? String(s.sesSeq) : '',
    sdSeq: sd?.sdSeq,
    ownerId: '',
    isDeleted: status !== '' && status !== 'ACTIVE',
    createdDate: '',
    createdById: '',
    lastModifiedDate: '',
    lastModifiedById: '',
    systemModstamp: '',
    lastViewedDate: '',
    lastReferencedDate: '',
    instructor,
    course: clsSeq,
    isActive,
    startTime: parseApiDateTime(startRaw),
    cutoffDays: s.cutoffDays,
    location: locationParts.join(', '),
    capacity: s.capacity ?? 0,
    countGuestsInCapacity: false,
    sessionType: (s.typeDescription ?? s.type ?? '').trim(),
    duration: (sd?.durationDescription ?? sd?.duration ?? '').trim(),
    cost: classCost ?? 0,
    meetingLink: (sd?.meetingLink ?? '').trim(),
    booked: s.registrations,
    addStreet: street,
    addCity: city,
    addState: state,
    addPostalCode: postalCode,
    instructorFirstName: instructor,
    instructorLastName: ''
  };
}

export function mapClassSessionsToSessions(clsSeq: string, sessions: ApiClassSession[] | undefined, classCost?: number): Session[] {
  if (!Array.isArray(sessions) || !clsSeq.trim()) {
    return [];
  }
  return sessions
    .filter((s) => s.sesSeq != null)
    .map((s) => mapClassSessionToSession(clsSeq, s, classCost));
}

/**
 * Normalize embedded {@link ApiClassSession} or mapped {@link Session} rows.
 */
export function normalizeSessionsForClass(
  raw: ApiClassSession[] | Session[] | undefined,
  clsSeq: string,
  classCost?: number
): Session[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const first = raw[0];
  if (first && typeof first === 'object' && 'sesSeq' in first) {
    return mapClassSessionsToSessions(clsSeq, raw as ApiClassSession[], classCost);
  }
  return (raw as Session[]).map((s) => ({
    ...s,
    course: (clsSeq || s.course || '').trim(),
    addStreet: s.addStreet ?? '',
    addCity: s.addCity ?? '',
    addState: s.addState ?? '',
    addPostalCode: s.addPostalCode ?? '',
    instructorFirstName: s.instructorFirstName ?? '',
    instructorLastName: s.instructorLastName ?? ''
  }));
}

export function normalizeGetClassesResponse(data: unknown): ClassResponse {
  const d = data as GetClassesApiEnvelope | null | undefined;
  const rawList = extractClassRowList(d?.Classes ?? d?.classes);
  const classes = rawList.map((r) => classFromApiRow(r));
  return {
    classes,
    x_service: String(d?.x_service ?? ''),
    x_status: String(d?.x_status ?? ''),
    x_count: d?.x_count != null ? String(d.x_count) : '',
    x_msg: String(d?.x_msg ?? '')
  };
}
